//EXTERNAL
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useParams, useHistory, Link } from "react-router-dom";
//COMPONENTS
import * as ComponentLibrary from "@picnicmedia/masanatemples123";
import StoryFormat from "../components/formats/Story";
import PostFormat from "../components/formats/Post";
import CarouselFormat from "../components/formats/Carousel";
import SwipeFormat from "../components/formats/Swipe";
import ClipsFormat from "../components/formats/Clips";
import AdvertiserDropdown from "../components/AdvertiserDropdown";
//ASSETS
import placeholderBrandLogo from "../assets/placeholder-brandlogo.png";
import placeholderButtonLogo from "../assets/placeholder-buttonlogo.png";
import PlaceholderCreative from "../assets/placeholder-creative.png";
//HOOKS
import is_new_creative from "../hooks/is_new_creative";
import is_format_preview_sticky from "../hooks/is_format_preview_sticky";
//LIBS
import { save_file_locally } from "../libs/file_saver";
import { post_slack_message } from "../libs/slack";
import { s3_blob_download, analytics_record, s3_media_upload } from "../libs/amplify";
import { adform_creative_tags, zip_creative_files, ingest_package } from "../libs/node";
import { logger } from "../libs/logger";
import { Google_Analytics_Event } from "../libs/google-analytics";
//HELPERS
import set_creative_context_data from "../helpers/set_creative_context_data";
import add_brandlogo from "../helpers/add_brandlogo";
import edit_brandlogo from "../helpers/edit_brandlogo";
import add_asset from "../helpers/add_asset";
import add_button_logo from "../helpers/add_button_logo";
import edit_button_logo from "../helpers/edit_button_logo";
import validate_all_s3_urls from "../helpers/validate_s3_url";
import { validate_adform_url } from "../helpers/validate_form";
//CONTEXT;
import { useUserState } from "../contexts/user.context";
import { useCreativeState, useCreativeDispatch } from "../contexts/creative.context";
import { useNotificationDispatch } from "../contexts/notification.context";
import { useMediaLoadingDispatch } from "../contexts/mediaLoading.context";
//CONFIGS
import config from "../configs/config";
import studio_config from "../configs/studio.json";
//TYPES
export interface StudioConfig {
  [key: string]: any;
}

//QUERIES
import * as queries from "../graphql/queries";
//MUTATIONS
import * as mutations from "../graphql/mutations";
//FEATURES
import Reactions from "../features/creative/reactions/components/Reactions";
import ExtraFeatures from "../features/creative/extra-features/components/ExtraFeatures";
import { SaveButton } from "../features/creative/save/functions/SaveButton";
import { ExportPngButton } from "../features/creative/export-png";
import { PreviewButton } from "../features/creative/preview";
import { AudioIconPosition, CountdownIconPosition } from "../features/creative/icon-positions";
import { FontUpload } from "../features/creative/font-upload";
import { NPS } from "../features/notifications/nps";
import { DynamicFormatPreview } from "../features/creative/dynamic-format-preview";
import { SocialExtension } from "../features/creative/social-extension";
import { FrameRatioBar } from "../features/creative/frame-ratio";
import { AdSize } from "../features/creative/ad-size";
import { FormatMenu } from "../features/creative/format-menu";
import { ContentDescription } from "../features/creative/content-description";
import { BuildMethodToggle, CustomPackagePanel, CustomPackagePreview, inspectPackage, buildBlobPreview, BuildMethod, CustomPackageState } from "../features/creative/custom-package";

export default function Creative() {
  const { creativeId } = useParams<Record<string, string | undefined>>();
  const user = useUserState();
  const creative = useCreativeState();
  const creativeDispatch = useCreativeDispatch();
  const notificationDispatch = useNotificationDispatch();
  const mediaLoadingDispatch = useMediaLoadingDispatch();
  const studioConfig: StudioConfig = studio_config;
  const isNewCreative = is_new_creative();
  const isFormatPreviewSticky = is_format_preview_sticky();
  const firstSlide = creative.metadata.slides[0];
  const [creativeLoading, setCreativeLoading] = useState(false);
  const [displayColorPickerCtaButton, setDisplayColorPickerCtaButton] = useState(false);
  const [displayColorPickerCtaText, setDisplayColorPickerCtaText] = useState(false);
  const [displayColorPickerIcons, setDisplayColorPickerIcons] = useState(false);
  const [brandLogoModalState, setBrandLogoModalState] = useState(false);
  const [buttonLogoModalState, setButtonLogoModalState] = useState(false);
  const [requestTagsModalState, setRequestTagsModalState] = useState(false);
  const [requestTagsSizeModalState, setRequestTagsSizeModalState] = useState(false);
  const emptyAdformTaggingInput = {
    impressionPixel: { value: "", valid: true, errorMessage: "" },
    clickPixel: { value: "", valid: true, errorMessage: "" },
    javascriptUrl: { value: "", valid: true, errorMessage: "" },
    destinationUrl: { value: "", valid: false, errorMessage: "" },
  };
  const [adformTaggingInputs, setAdformTaggingInputs] = useState(emptyAdformTaggingInput);
  const [requestTagsSnippetTextAreaValue, setRequestTagsSnippetTextAreaValue] = useState<string[]>([]);
  const [requestTagsSnippetCopySuccess, setRequestTagsSnippetCopySuccess] = useState(false);
  const [requestTagsToggleSelectedValue, setRequestTagsToggleSelectedValue] = useState("Short Tags (default)");
  const [adSize, setAdSize] = useState("Picnic (320:480)");
  const [applicableFormats, setApplicableFormat] = useState(["post", "carousel", "story", "clips"]);
  // Custom (bring-your-own package) build method — the fork before Ad Size. JH 30-07-26
  const history = useHistory();
  const [buildMethod, setBuildMethod] = useState<BuildMethod>("template");
  const [customPackage, setCustomPackage] = useState<CustomPackageState | null>(null);
  const [customInspecting, setCustomInspecting] = useState(false);
  const [customSaving, setCustomSaving] = useState(false);

  // Inspect a dropped/selected package zip client-side and build a pre-save preview. Nothing is
  // uploaded here — that happens on Save (handleCustomSave).
  const handleCustomFileSelect = async (file: File) => {
    customPackage?.revokePreview();
    setCustomPackage(null);
    setCustomInspecting(true);
    try {
      const inspection = await inspectPackage(file);
      let previewUrl: string | null = null;
      let revokePreview = () => {};
      if (inspection.valid) {
        try {
          const preview = await buildBlobPreview(file);
          previewUrl = preview.url;
          revokePreview = preview.revoke;
        } catch {
          /* preview is best-effort; validation already passed */
        }
      }
      setCustomPackage({ file, inspection, previewUrl, revokePreview });
    } finally {
      setCustomInspecting(false);
    }
  };

  const handleCustomClear = () => {
    customPackage?.revokePreview();
    setCustomPackage(null);
  };

  const handleBuildMethodChange = (method: BuildMethod) => {
    if (method === buildMethod) return;
    // Leaving Custom discards the staged package (nothing was uploaded).
    if (method === "template" && customPackage) {
      customPackage.revokePreview();
      setCustomPackage(null);
    }
    setBuildMethod(method);
  };

  // Save a custom package: validate the library inputs + package, upload the zip to S3, then ingest
  // (which unpacks it and writes the Creative record). No screenshots / slides / template mutations.
  const handleCustomSave = async (e: React.MouseEvent<Element, MouseEvent> | undefined) => {
    (e?.currentTarget as HTMLElement)?.blur();
    const fail = (message: string) => notificationDispatch({ type: "ADD", payload: { header: "Can't save package", message, type: "error", autoDismiss: false } });
    if (!creative.advertiserName) return fail("Advertiser is not selected");
    if (!creative.adName) return fail("Ad name is not completed");
    if (!/^[A-Za-z0-9-_ ]*$/.test(creative.adName)) return fail("Ad name contains invalid characters");
    if (!customPackage) return fail("Choose a package .zip to upload");
    if (!customPackage.inspection.valid) return fail("Package isn't valid — fix the issues listed and try again");

    setCustomSaving(true);
    mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: true, type: "upload", message: "Uploading package", progress: Math.floor(Math.random() * (30 - 11 + 1) + 11) } });
    try {
      await s3_media_upload({ fileKey: "source-upload.zip", mimeType: "application/zip", body: customPackage.file, teamId: user.teamId, creativeId });
      mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: true, type: "upload", message: "Ingesting package", progress: Math.floor(Math.random() * (80 - 61 + 1) + 61) } });
      const result = await ingest_package({
        teamId: user.teamId,
        advertiserId: creative.advertiserId,
        advertiserName: creative.advertiserName,
        adName: creative.adName,
        authorId: user.id,
        authorEmail: user.email,
        sourceZipKey: `public/${user.teamId}/${creativeId}/source-upload.zip`,
        creativeId,
      });
      Google_Analytics_Event({ name: "package_uploaded", category: "ad_creative", action: "ingest", label: creativeId });
      const warnings: string[] = result?.warnings || [];
      mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
      notificationDispatch({ type: "ADD", payload: { header: "Package saved", message: warnings.length ? `Added to your library with ${warnings.length} warning${warnings.length > 1 ? "s" : ""}: ${warnings.join("; ")}` : "Added to your library.", type: "success", autoDismiss: !warnings.length } });
      customPackage.revokePreview();
      history.push(`/library/${user.teamId}`);
    } catch (error: any) {
      mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
      const payload = error?.response?.data?.payload;
      const message = (payload && Array.isArray(payload.problems) && payload.problems.join("; ")) || (typeof payload === "string" && payload) || error?.message || "Upload failed. Please try again.";
      logger({ error, user, context: { origin: "Creative_handleCustomSave" } });
      notificationDispatch({ type: "ADD", payload: { header: "Error saving package", message, type: "error", autoDismiss: false } });
    } finally {
      setCustomSaving(false);
    }
  };

  const [updateCreativeAdform] = useMutation(mutations.UPDATE_CREATIVE_ADSERVER);
  const requestTags = `Request Tags`;
  const requestSavedTags = ["Short Tags (default)", "Long Tags"];
  const selectedTag = requestTagsToggleSelectedValue === "Short Tags (default)" ? 0 : 1;

  const {
    error: LTAError,
    loading: LTALoading,
    data: LTAData,
  } = useQuery(queries.LIST_TEAM_ADVERTISERS, {
    variables: { teamId: user.teamId },
    onError: (error) => {
      logger({ error, user, context: { origin: "Creative_LIST_TEAM_ADVERTISERS" } });
      Google_Analytics_Event({ name: "error", category: error.message + " " + error.cause, action: "Creative_LIST_TEAM_ADVERTISERS", label: user.email });
    },
  });
  const {
    error: GECError,
    loading: GECLoading,
    data: GECData,
  } = useQuery(queries.GET_EXISTING_CREATIVE, {
    variables: { id: creativeId, teamId: user.teamId },
    skip: isNewCreative,
    onCompleted: async (existingCreativeData) => {
      try {
        if (isNewCreative) return;
        //
        // (07-11-22 HTL) If no creative ID within the team is found, return an error.
        // (03-05-23 HTL) This is causing the issue, have commented out until there's a better solution for ristricting creatives to teams.
        //
        // console.log(existingCreativeData);
        // if (!existingCreativeData.readOneCreativeId) {
        //
        // (08-11-22 HTL) Push user to unhandled route which will then load the 404 NotFound.tsx container.
        //
        //   history.push("/not-found");
        //   throw new Error("Creative doesn't exist in your team.");
        // }
        setCreativeLoading(true);
        await set_creative_context_data({ GECData: existingCreativeData, creativeDispatch, creativeId: creativeId! });
        setCreativeLoading(false);
      } catch (error) {
        setCreativeLoading(false);
        notificationDispatch({
          type: "ADD",
          payload: {
            header: "Error",
            message: error.message,
            type: "error",
            autoDismiss: true,
          },
        });
        console.log(error);
      }
    },
    onError: (error) => {
      logger({ error, user, context: { origin: "Creative_GET_EXISTING_CREATIVE" } });
      Google_Analytics_Event({ name: "error", category: error.message + " " + error.cause, action: "Creative_GET_EXISTING_CREATIVE", label: user.email });
    },
  });
  const { error: GTAError, loading: GTALoading, data: GTAData } = useQuery(queries.GET_TEAM_ADSERVERS_ADFORM_ADVERTISERID, { variables: { id: user.teamId } });

  useEffect(() => {
    creativeDispatch({ type: "UPDATE_CREATIVE_CREATIVEID", payload: { creativeId: creativeId! } });
  }, [creativeId]);

  const downloadAdformTags = async (adformTagFileName: string, tagType: number) => {
    if (config.analytics.DISABLED === false && user.teamId !== "pi6j") {
      analytics_record({
        name: `request-download-${tagType === 0 ? "short" : "long"}`,
        attributes: {
          eventType: "click",
          userId: `${user.id}`,
          userEmail: `${user.email}`,
          teamId: `${user.teamId}`,
          teamName: `${user.teamName}`,
          advertiserId: `${creative.advertiserId}`,
          advertiserName: `${creative.advertiserName}`,
          creativeId: `${creativeId}`,
          adName: `${creative.adName}`,
          brandName: `${creative.metadata.brandName}`,
          format: `${creative.metadata.format}`,
          assetType: `${creative.metadata.slides.find((slide: any) => slide.media.mimeType.includes("video")) ? "video" : "image"}`,
        },
      });
    }
    try {
      // Get the Adform scriptTagFile as blob (as run into permissions issues/403 errors when using save_file_locally with s3 signed URL).
      let scriptTagFile = await s3_blob_download({ teamId: user.teamId, creativeId: creativeId, fileName: adformTagFileName });
      save_file_locally(scriptTagFile, adformTagFileName);
      notificationDispatch({
        type: "ADD",
        payload: {
          header: "Success",
          message: "Tags downloaded",
          type: "success",
          autoDismiss: true,
        },
      });
    } catch (error) {
      console.log(error);
    }
  };

  const getAdformTagsContent = async (adformTagFileName: string) => {
    try {
      // Get the Adform scriptTagFile as blob (as run into permissions issues/403 errors when using save_file_locally with s3 signed URL).
      let scriptTagFile = await s3_blob_download({ teamId: user.teamId, creativeId: creativeId, fileName: adformTagFileName });
      const scriptTagFileContent = await (scriptTagFile as Blob)?.text();
      return scriptTagFileContent.replace(/^\s*$(?:\r\n?|\n)/gm, ""); // Remove empty first line of content
    } catch (error) {
      console.log(error);
    }
  };

  const copyAdFormContentToClipboard = async (e: React.MouseEvent<Element, MouseEvent> | undefined, text: string, tagType: number) => {
    (e?.currentTarget as HTMLElement).blur();
    if (config.analytics.DISABLED === false && user.teamId !== "pi6j") {
      analytics_record({
        name: `request-copied-${tagType === 0 ? "short" : "long"}`,
        attributes: {
          eventType: "click",
          userId: `${user.id}`,
          userEmail: `${user.email}`,
          teamId: `${user.teamId}`,
          teamName: `${user.teamName}`,
          advertiserId: `${creative.advertiserId}`,
          advertiserName: `${creative.advertiserName}`,
          creativeId: `${creativeId}`,
          adName: `${creative.adName}`,
          brandName: `${creative.metadata.brandName}`,
          format: `${creative.metadata.format}`,
          assetType: `${creative.metadata.slides.find((slide: any) => slide.media.mimeType.includes("video")) ? "video" : "image"}`,
        },
      });
    }
    try {
      await navigator.clipboard.writeText(text);
      setRequestTagsSnippetCopySuccess(true);
      notificationDispatch({
        type: "ADD",
        payload: {
          header: "Tag copied!",
          message: "",
          type: "success",
          autoDismiss: true,
        },
      });
    } catch (error) {
      setRequestTagsSnippetCopySuccess(false);
      notificationDispatch({
        type: "ADD",
        payload: {
          header: "Error copying tag",
          message: error.response ? error.response.data.payload : error.message,
          type: "error",
          autoDismiss: false,
        },
      });
    }
  };

  const requestAdformTag = async (e: any) => {
    e.currentTarget.blur();
    try {
      // Check if creative has video assets as used in Adform banner name
      const hasVideo = creative.metadata.slides.filter((slide: any) => slide.media.mimeType.includes("video")).length > 0;
      if (!GTAData.readOneTeamId.adservers.adform.advertiserId) throw new Error("Advertiser id not found");
      mediaLoadingDispatch({
        type: "UPDATE_LOADING_STATE",
        payload: { status: true, type: "adform", message: "Requesting tags", progress: Math.floor(Math.random() * (30 - 11 + 1) + 11) },
      });
      // Run the adform creative tags lambda and return tags data (including file name in s3)
      const adformTagsData = await adform_creative_tags({
        creativeId,
        teamId: user.teamId,
        advertiserId: GTAData.readOneTeamId.adservers.adform.advertiserId,
        advertiserName: creative.advertiserName,
        format: creative.metadata.format,
        adName: creative.adName,
        hasVideo,
        clickout: creative.metadata.button.clickout,
        impressionPixel: adformTaggingInputs.impressionPixel.value,
        clickPixel: adformTaggingInputs.clickPixel.value,
        javascriptUrl: adformTaggingInputs.javascriptUrl.value,
        destinationUrl: adformTaggingInputs.destinationUrl.value,
      });
      mediaLoadingDispatch({
        type: "UPDATE_LOADING_STATE",
        payload: { status: true, type: "adform", message: "Requesting tags", progress: Math.floor(Math.random() * (80 - 61 + 1) + 61) },
      });
      let { campaignId, adformTagId } = adformTagsData.adformCreativeTagsIds;
      // convert campaign id to string as currently a number
      campaignId = campaignId.toString();
      // Update DynamoDB creative table & creative context. Currently the tagsFileName is set as an array (with a single item), to support multiple tags per creative in future.
      // Updating adserver.name ensures that if any tags are requested for creatives that still have the old adserver string property, its Creative dynamoDB item is updated with the new adserver object - KT 02.08.22
      await updateCreativeAdform({
        variables: { name: "adform", id: creativeId, campaignId, adformTagId, tagFileNames: adformTagsData.adformCreativeTagsFileName },
      });
      creativeDispatch({
        type: "UPDATE_CREATIVE_ADSERVER_NAME",
        payload: { name: "adform" },
      });
      creativeDispatch({
        type: "UPDATE_CREATIVE_ADSERVER_CAMPAIGNID",
        payload: { campaignId },
      });
      creativeDispatch({
        type: "UPDATE_CREATIVE_ADSERVER_TAGID",
        payload: { tagId: adformTagId },
      });
      creativeDispatch({
        type: "UPDATE_CREATIVE_ADSERVER_TAGFILENAMES",
        payload: { tagFileNames: adformTagsData.adformCreativeTagsFileName },
      });
      mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
      setRequestTagsModalState(!requestTagsModalState);
      setRequestTagsSizeModalState(!requestTagsSizeModalState);
      // Get content of Adform tags
      const adformShortTagContent = await getAdformTagsContent(adformTagsData.adformCreativeTagsFileName[0]);
      const adformLongTagContent = await getAdformTagsContent(adformTagsData.adformCreativeTagsFileName[1]);
      const adformTagContent = [adformShortTagContent, adformLongTagContent] as string[];
      setRequestTagsSnippetTextAreaValue(adformTagContent);
    } catch (error) {
      mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
      notificationDispatch({
        type: "ADD",
        payload: {
          header: "Error requesting tags",
          message: error.response ? error.response.data.payload : error.message,
          type: "error",
          autoDismiss: false,
        },
      });
    }
  };

  //
  // REQUEST TAG BUTTON
  //
  const handleRequestTag = async (e: any, tagType: string) => {
    e.currentTarget.blur();
    Google_Analytics_Event({ name: "tag_requested", category: "ad_creative", action: "request", label: creativeId });
    if (config.analytics.DISABLED === false && user.teamId !== "pi6j") {
      analytics_record({
        name: "request",
        attributes: {
          eventType: "click",
          userId: `${user.id}`,
          userEmail: `${user.email}`,
          teamId: `${user.teamId}`,
          teamName: `${user.teamName}`,
          advertiserId: `${creative.advertiserId}`,
          advertiserName: `${creative.advertiserName}`,
          creativeId: `${creativeId}`,
          adName: `${creative.adName}`,
          brandName: `${creative.metadata.brandName}`,
          format: `${creative.metadata.format}`,
          assetType: `${creative.metadata.slides.find((slide: any) => slide.media.mimeType.includes("video")) ? "video" : "image"}`,
          previewURL: `https://${config.s3.BUCKET}.s3.${config.s3.REGION}.amazonaws.com/public/${user.teamId}/${creativeId}/index.min.html`,
        },
      });
    }
    try {
      const zipFilesArray = await zip_creative_files({ creativeId, teamId: user.teamId });
      // If zip > 5MB (limit set by Product), throw error. Adform zips are marginally larger than dv360 (so use Adform zip for check). KT 11.01.22
      const adformZip = zipFilesArray.find((zip: any) => zip.adserver === "adform");
      if (adformZip.size > studioConfig.files.maxBodyLength) throw new Error(`File size (${(adformZip.size / (1024 * 1024)).toFixed(1)}MB) too big - can't exceed 5MB.`);
      // Request Tags path for picnic users - checks for stag/prod & dev Picnic teamIds
      if (config.managedServiceTeamIds.includes(user.teamId)) {
        await post_slack_message({
          zipFilesArray,
          advertiserName: creative.advertiserName,
          adName: creative.adName,
          teamName: user.teamName,
          email: user.email,
        });
        notificationDispatch({
          type: "ADD",
          payload: {
            header: "Tags requested",
            message: "",
            type: "success",
            autoDismiss: true,
          },
        });
      }
      if (!config.managedServiceTeamIds.includes(user.teamId)) {
        // If tags already exist, user has option to download short or full version of that tag, if not - open the Adform modal.
        if (tagType === requestTags) {
          if (creative.adserver.tagId) {
            setRequestTagsSizeModalState(!requestTagsSizeModalState);
            const adformShortTagContent = await getAdformTagsContent(creative.adserver.tagFileNames[0]);
            const adformLongTagContent = await getAdformTagsContent(creative.adserver.tagFileNames[1]);
            const adformTagContent = [adformShortTagContent, adformLongTagContent] as string[];
            setRequestTagsSnippetTextAreaValue(adformTagContent);
          } else {
            await post_slack_message({ zipFilesArray, advertiserName: creative.advertiserName, adName: creative.adName, teamName: user.teamName, email: user.email });
            setRequestTagsModalState(!requestTagsModalState);
          }
        } else if (requestSavedTags) {
          downloadAdformTags(creative.adserver.tagFileNames[tagType === "Short Tags (default)" ? 0 : 1], selectedTag);
        }
      }
    } catch (error) {
      let errorMessage = "";
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      logger({ error, user, context: { origin: "Creative_handleRequestTag" } });
      Google_Analytics_Event({ name: "error", category: error.message + " " + error.cause, action: "Creative_handleRequestTag", label: user.email });
      notificationDispatch({
        type: "ADD",
        payload: {
          header: "Error requesting tags",
          message: errorMessage,
          type: "error",
          autoDismiss: false,
        },
      });
    }
  };

  //
  // RENDER SPECIFIC FORMAT FRAMES SECTION
  //
  const renderChosenFormatFramesSection = () => {
    switch (creative.metadata.format) {
      case "story": {
        return <StoryFormat />;
      }
      case "post": {
        return <PostFormat />;
      }
      case "carousel": {
        return <CarouselFormat />;
      }
      case "swipe": {
        return <SwipeFormat />;
      }
      case "clips": {
        return <ClipsFormat />;
      }
      default: {
        return <></>;
      }
    }
  };

  //
  // VALIDATE ALL S3 URLS EVERY TEN MINS
  //
  useEffect(() => {
    const interval = setInterval(() => {
      validate_all_s3_urls(creative, user.teamId, creativeDispatch);
    }, 600000);
    return () => clearInterval(interval);
  }, [creative]);

  return (
    <>
      {LTALoading || GECLoading || creativeLoading ? (
        <div className="w-100 h-100 justify-center background-color-neutral-2">
          <ComponentLibrary.PCSpinner />
        </div>
      ) : (
        <div className="background-color-neutral-2">
          <ComponentLibrary.PCSpacer type={"padding"} position={"around"} size={5}>
            <ComponentLibrary.PCSpacer type={"padding"} position={"left"} size={6}>
              <ComponentLibrary.PCSpacer type={"padding"} position={"right"} size={6}>
                <div className="flex flex-column items-center">
                  {/*  */}
                  {/* TOP BAR */}
                  {/*  */}
                  <div className="flex w-100">
                    <div className="pointer">
                      <Link
                        to={{
                          pathname: LTAData.readAllCreativesTeam.filter((item: any) => item.advertiserId === creative.advertiserId).length > 0 ? `/library/${user.teamId}/${creative.advertiserId}/${creative.advertiserName}` : `/library/${user.teamId}`,
                        }}
                        style={{ color: "inherit", textDecoration: "none" }}>
                        <ComponentLibrary.PCIconPicker icon={"arrow-left"} />
                      </Link>
                    </div>
                    <ComponentLibrary.PCSpacer type={"padding"} position={"left"} size={3} />
                    <ComponentLibrary.PCHeader text={"Ad Creator"} />
                    {buildMethod === "custom" ? (
                      <>
                        <div className="flex-auto" />
                        <ComponentLibrary.PCActionButton
                          isDisabled={customSaving || !customPackage?.inspection.valid || !creative.advertiserName || !creative.adName}
                          isLoading={customSaving}
                          text={customSaving ? "Saving…" : "Save package"}
                          iconLeft={"check"}
                          color={"background-color-primary-3"}
                          textColor={"white"}
                          clickHandler={handleCustomSave}
                        />
                      </>
                    ) : (
                      <>
                        <ExportPngButton />
                        <ComponentLibrary.PCSpacer type={"padding"} position={"left"} size={3} />
                        <PreviewButton
                          isTruncated={false}
                          data={{
                            advertiserId: creative.advertiserId,
                            advertiserName: creative.advertiserName,
                            id: creativeId,
                            adName: creative.adName,
                            metadata: {
                              aspectRatio: creative.metadata.aspectRatio,
                              brandName: creative.metadata.brandName,
                              format: creative.metadata.format,
                              mimeTypes: creative.metadata.slides.map((x) => x.media.mimeType),
                            },
                          }}
                        />
                        <ComponentLibrary.PCSpacer type={"padding"} position={"left"} size={3} />
                        <SaveButton />
                        <ComponentLibrary.PCSpacer type={"padding"} position={"left"} size={3} />
                        <ComponentLibrary.PCActionButton isDisabled={isNewCreative} text={"Request Tags"} color={"background-color-primary-3"} textColor={"white"} iconRight={"arrow-right"} clickHandler={(e) => handleRequestTag(e, requestTags)} />
                      </>
                    )}
                  </div>
                </div>
                <ComponentLibrary.PCSpacer type={"padding"} position={"top"} size={5} />
                <div className="flex flex-column justify-center w-100">
                  <div className="flex flex-row w-100">
                    {/*  */}
                    {/* LEFT COLUMN */}
                    {/*  */}
                    <div id="creative-left-column" className="w-50">
                      {/*  */}
                      {/* BASIC DETAILS */}
                      {/*  */}
                      <ComponentLibrary.PCContainerMain color={"background-color-neutral-1"}>
                        <ComponentLibrary.PCSpacer type={"padding"} position={"around"} size={4}>
                          <ComponentLibrary.PCHeader text={"New Ad"} />
                          <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                          <ComponentLibrary.PCDivider />
                          <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                          <AdvertiserDropdown advertiserList={LTAData.readAllCreativesTeam} />
                          <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                          <div className="w-50">
                            <ComponentLibrary.PCInput
                              label={"Ad name"}
                              placeholder={"Ad name"}
                              type={"text"}
                              value={creative.adName}
                              handleChange={(e: any) => {
                                const { value } = e.target;
                                creativeDispatch({
                                  type: "UPDATE_CREATIVE_ADNAME",
                                  payload: { adName: value },
                                });
                              }}
                            />
                          </div>
                        </ComponentLibrary.PCSpacer>
                      </ComponentLibrary.PCContainerMain>
                      <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={3} />

                      {/* BUILD METHOD FORK — Template Library vs Custom, before Ad Size (new ad only). JH 30-07-26 */}
                      {isNewCreative && (
                        <>
                          <BuildMethodToggle value={buildMethod} onChange={handleBuildMethodChange} />
                          <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={3} />
                        </>
                      )}

                      {buildMethod === "custom" ? (
                        <CustomPackagePanel pkg={customPackage} busy={customInspecting} onSelectFile={handleCustomFileSelect} onClear={handleCustomClear} />
                      ) : (
                        <>
                      {/*  */}
                      {/* FORMAT */}
                      {/*  */}
                      <ComponentLibrary.PCContainerMain borderRadiusDirection={"top"}>
                        <ComponentLibrary.PCSpacer type={"padding"} position={"around"} size={4}>
                          {/* <SocialExtension /> */}
                          <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={6} />
                          <AdSize updateAdSize={(adSize) => setAdSize(adSize)} applicableFormats={(applicableFormats: string[]) => setApplicableFormat(applicableFormats)} />
                          <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={6} />
                          <FormatMenu applicableFormats={applicableFormats} adSize={adSize} />
                        </ComponentLibrary.PCSpacer>
                      </ComponentLibrary.PCContainerMain>
                      {/*  */}
                      {/* FEATURES */}
                      {/*  */}
                      <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={1} />
                      <ExtraFeatures>
                        <Reactions />
                      </ExtraFeatures>
                      <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={3} />

                      {/*  */}
                      {/* BRAND */}
                      {/*  */}
                      <ComponentLibrary.PCContainerMain>
                        <ComponentLibrary.PCSpacer type={"padding"} position={"around"} size={4}>
                          <ComponentLibrary.PCHeader text={"Brand"} />
                          <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                          {creative.metadata.brandLogo.bodyCropped === "" ? (
                            <ComponentLibrary.PCDragAndDrop
                              label={"Brand logo"}
                              clickHandler={(e: any) => {
                                e.target.value = null;
                              }}
                              changeHandler={async (e: any) => {
                                try {
                                  [...e.target.files].forEach(async (file: any, i: any) => {
                                    e.preventDefault();
                                    if (i > 0) return;
                                    mediaLoadingDispatch({
                                      type: "UPDATE_LOADING_STATE",
                                      payload: {
                                        status: true,
                                        type: "logo",
                                        message: "Uploading logo",
                                        progress: Math.floor(Math.random() * (10 - 3 + 1) + 3),
                                      },
                                    });
                                    await add_brandlogo({
                                      file,
                                      formatType: creative.metadata.format,
                                      teamId: user.teamId,
                                      creativeId,
                                      creativeDispatch,
                                      mimeType: file.type,
                                      name: file.name,
                                      notificationDispatch,
                                      mediaLoadingDispatch,
                                    });
                                    mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
                                  });
                                } catch (error) {
                                  mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
                                  logger({ error, user, context: { origin: "Creative_PCDragAndDrop_changeHandler_brandLogo" } });
                                  Google_Analytics_Event({ name: "error", category: error.message + " " + error.cause, action: "Creative_PCDragAndDrop_changeHandler_brandLogo", label: user.email });
                                  return notificationDispatch({
                                    type: "ADD",
                                    payload: {
                                      header: "Error",
                                      message: "Something went wrong.",
                                      type: "error",
                                      autoDismiss: false,
                                    },
                                  });
                                }
                              }}
                              dropHandler={(e: any) => {
                                try {
                                  [...e.dataTransfer.files].forEach(async (file: any, i: any) => {
                                    mediaLoadingDispatch({
                                      type: "UPDATE_LOADING_STATE",
                                      payload: {
                                        status: true,
                                        type: "logo",
                                        message: "Uploading logo",
                                        progress: Math.floor(Math.random() * (10 - 3 + 1) + 3),
                                      },
                                    });
                                    e.preventDefault();
                                    if (i > 0) return;
                                    await add_brandlogo({
                                      file,
                                      formatType: creative.metadata.format,
                                      teamId: user.teamId,
                                      creativeId,
                                      creativeDispatch,
                                      mimeType: file.type,
                                      name: file.name,
                                      notificationDispatch,
                                      mediaLoadingDispatch,
                                    });
                                    mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
                                  });
                                } catch (error) {
                                  mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
                                  logger({ error, user, context: { origin: "Creative_PCDragAndDrop_dropHandler_brandLogo" } });
                                  Google_Analytics_Event({ name: "error", category: error.message + " " + error.cause, action: "Creative_PCDragAndDrop_dropHandler_brandLogo", label: user.email });
                                  return notificationDispatch({
                                    type: "ADD",
                                    payload: {
                                      header: "Error",
                                      message: "Something went wrong.",
                                      type: "error",
                                      autoDismiss: false,
                                    },
                                  });
                                }
                              }}
                              buttonText={"Browse"}
                              mediaAssetTitle={"Brand logo"}
                              mediaAllowed={[
                                {
                                  title: "Images",
                                  types: ["jpg", "png"],
                                  maxSize: "2MB",
                                },
                              ]}
                              multipleAllowed={false}
                            />
                          ) : (
                            <div className="flex">
                              <ComponentLibrary.PCPreviewImage height={creative.metadata.format === "swipe" ? "auto" : "128px"} width={creative.metadata.format === "swipe" ? "auto" : "128px"} maxWidth={"250px"} maxHeight={"200px"} backgroundImage={creative.metadata.brandLogo.bodyCropped} />
                              <ComponentLibrary.PCSpacer type={"margin"} position={"left"} size={5} />
                              <div className="flex flex-column justify-center">
                                <div className="flex">
                                  <ComponentLibrary.PCActionButton
                                    text={"Crop image"}
                                    clickHandler={(e: any) => {
                                      e.currentTarget.blur();
                                      setBrandLogoModalState(!brandLogoModalState);
                                    }}
                                    textColor={"color-text-5"}
                                    color={"platform-background-platform-background-4"}
                                  />
                                  <ComponentLibrary.PCSpacer type={"margin"} position={"left"} size={3} />
                                  <ComponentLibrary.PCDivider vertical={true} />
                                  <ComponentLibrary.PCSpacer type={"margin"} position={"left"} size={3} />
                                  <div className="upload-button-wrapper dim relative overflow-hidden dib-ns">
                                    <input
                                      type="file"
                                      className="o-0 absolute top-0 left-0 h-100"
                                      style={{
                                        cursor: "pointer",
                                      }}
                                      name="brand_logo_upload"
                                      id="brand_logo_upload"
                                      onChange={(e: any) => {
                                        mediaLoadingDispatch({
                                          type: "UPDATE_LOADING_STATE",
                                          payload: {
                                            status: true,
                                            type: "upload",
                                            message: "Uploading logo",
                                            progress: Math.floor(Math.random() * (10 - 3 + 1) + 3),
                                          },
                                        });
                                        try {
                                          add_brandlogo({
                                            file: e.target.files[0],
                                            formatType: creative.metadata.format,
                                            teamId: user.teamId,
                                            creativeId,
                                            creativeDispatch,
                                            mimeType: e.target.files[0].type,
                                            name: e.target.files[0].name,
                                            notificationDispatch,
                                            mediaLoadingDispatch,
                                          });
                                          mediaLoadingDispatch({
                                            type: "UPDATE_LOADING_STATE",
                                            payload: { status: false, type: "", message: "", progress: 0 },
                                          });
                                        } catch (error) {
                                          mediaLoadingDispatch({
                                            type: "UPDATE_LOADING_STATE",
                                            payload: { status: false, type: "", message: "", progress: 0 },
                                          });
                                          logger({ error, user, context: { origin: "Creative_brand_logo_upload" } });
                                          Google_Analytics_Event({ name: "error", category: error.message + " " + error.cause, action: "Creative_brand_logo_upload", label: user.email });
                                          return notificationDispatch({
                                            type: "ADD",
                                            payload: {
                                              header: "Error",
                                              message: "Something went wrong.",
                                              type: "error",
                                              autoDismiss: false,
                                            },
                                          });
                                        }
                                      }}
                                      onClick={(e: any) => {
                                        e.target.value = null;
                                      }}
                                    />
                                    <ComponentLibrary.PCActionButton isDisabled={false} text={"Replace"} textColor={"color-text-5"} color={"platform-background-platform-background-4"} />
                                  </div>
                                  <ComponentLibrary.PCSpacer type={"margin"} position={"left"} size={3} />
                                  <ComponentLibrary.PCActionButton
                                    text={<ComponentLibrary.PCIconPicker icon={"trash"} />}
                                    textColor={"color-text-5"}
                                    color={"platform-background-platform-background-4"}
                                    isDisabled={!creative.metadata.brandLogo.bodyCropped}
                                    clickHandler={(e: any) => {
                                      e.currentTarget.blur();
                                      creativeDispatch({
                                        type: "UPDATE_CREATIVE_METADATA_BRANDLOGO_CROPPEDAREAPIXELS_WIDTH",
                                        payload: { width: studioConfig.formats[creative.metadata.format].brandLogo.width },
                                      });
                                      creativeDispatch({
                                        type: "UPDATE_CREATIVE_METADATA_BRANDLOGO_CROPPEDAREAPIXELS_HEIGHT",
                                        payload: { height: studioConfig.formats[creative.metadata.format].brandLogo.height },
                                      });
                                      creativeDispatch({
                                        type: "UPDATE_CREATIVE_METADATA_BRANDLOGO_ZOOM",
                                        payload: { zoom: 1 },
                                      });
                                      creativeDispatch({
                                        type: "UPDATE_CREATIVE_METADATA_BRANDLOGO_CROPPEDAREAPIXELS_X",
                                        payload: { x: 0 },
                                      });
                                      creativeDispatch({
                                        type: "UPDATE_CREATIVE_METADATA_BRANDLOGO_CROPPEDAREAPIXELS_Y",
                                        payload: { y: 0 },
                                      });
                                      creativeDispatch({
                                        type: "UPDATE_CREATIVE_METADATA_BRANDLOGO_MIMETYPE",
                                        payload: { mimeType: "" },
                                      });
                                      creativeDispatch({
                                        type: "UPDATE_CREATIVE_METADATA_BRANDLOGO_ID",
                                        payload: { id: "" },
                                      });
                                      creativeDispatch({
                                        type: "UPDATE_CREATIVE_METADATA_BRANDLOGO_FILENAME",
                                        payload: { fileName: "" },
                                      });
                                      creativeDispatch({
                                        type: "UPDATE_CREATIVE_METADATA_BRANDLOGO_BODYORIGINAL",
                                        payload: { bodyOriginal: "" },
                                      });
                                      creativeDispatch({
                                        type: "UPDATE_CREATIVE_METADATA_BRANDLOGO_BODYCROPPED",
                                        payload: { bodyCropped: "" },
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                          <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={5} />
                          <ComponentLibrary.PCDivider width={"w-100"} />
                          <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                          <div className="w-50">
                            <ComponentLibrary.PCInput
                              value={creative.metadata.brandName}
                              label={"Brand name"}
                              placeholder={"Brand name"}
                              type={"text"}
                              subHeader={"25 characters max"}
                              maxChar={25}
                              characterCounter={true}
                              isError={false}
                              errorMessage={""}
                              handleChange={(e: any) => {
                                const { value } = e.target;
                                creativeDispatch({
                                  type: "UPDATE_CREATIVE_METADATA_BRANDNAME",
                                  payload: { brandName: value },
                                });
                              }}
                            />
                          </div>
                        </ComponentLibrary.PCSpacer>
                      </ComponentLibrary.PCContainerMain>
                      <div className="relative">
                        <ComponentLibrary.PCDialogueBox open={brandLogoModalState} width={"width-13"} height={"height-13"} onClose={() => setBrandLogoModalState(!brandLogoModalState)}>
                          <>
                            <ComponentLibrary.PCCropper
                              media={creative.metadata.brandLogo.bodyOriginal || placeholderBrandLogo}
                              // Checks if format is defined - this ensures the component renders even if the format is undefined (e.g. if new creative).
                              cropShape={creative.metadata.format ? studioConfig.formats[creative.metadata.format].brandLogo.cropShape : "round"}
                              mimeType={creative.metadata.brandLogo.mimeType}
                              crop={creative.metadata.brandLogo.crop}
                              zoom={creative.metadata.brandLogo.zoom}
                              aspect={creative.metadata.format === "swipe" ? creative.metadata.brandLogo.dimensions.width / creative.metadata.brandLogo.dimensions.height : 1 / 1}
                              onCropChange={(crop: any) => {
                                creativeDispatch({
                                  type: "UPDATE_CREATIVE_METADATA_BRANDLOGO_CROP_X",
                                  payload: { x: crop.x },
                                });
                                creativeDispatch({
                                  type: "UPDATE_CREATIVE_METADATA_BRANDLOGO_CROP_Y",
                                  payload: { y: crop.y },
                                });
                              }}
                              onZoomChange={(zoom: any) => {
                                creativeDispatch({
                                  type: "UPDATE_CREATIVE_METADATA_BRANDLOGO_ZOOM",
                                  payload: { zoom },
                                });
                              }}
                              onCropComplete={(croppedArea: any, croppedAreaPixels: any) => {
                                creativeDispatch({
                                  type: "UPDATE_CREATIVE_METADATA_BRANDLOGO_CROPPEDAREAPIXELS_WIDTH",
                                  payload: { width: croppedAreaPixels.width },
                                });
                                creativeDispatch({
                                  type: "UPDATE_CREATIVE_METADATA_BRANDLOGO_CROPPEDAREAPIXELS_HEIGHT",
                                  payload: { height: croppedAreaPixels.height },
                                });
                                creativeDispatch({
                                  type: "UPDATE_CREATIVE_METADATA_BRANDLOGO_CROPPEDAREAPIXELS_X",
                                  payload: { x: croppedAreaPixels.x },
                                });
                                creativeDispatch({
                                  type: "UPDATE_CREATIVE_METADATA_BRANDLOGO_CROPPEDAREAPIXELS_Y",
                                  payload: { y: croppedAreaPixels.y },
                                });
                              }}
                              buttonClickHandler={async () => {
                                try {
                                  mediaLoadingDispatch({
                                    type: "UPDATE_LOADING_STATE",
                                    payload: { status: true, type: "crop", message: "Cropping logo", progress: Math.floor(Math.random() * (10 - 3 + 1) + 3) },
                                  });
                                  setBrandLogoModalState(false);
                                  await edit_brandlogo({
                                    method: "crop",
                                    teamId: user.teamId,
                                    creativeId: creative.creativeId,
                                    fileName: creative.metadata.brandLogo.fileName,
                                    fileId: creative.metadata.brandLogo.id,
                                    mimeType: creative.metadata.brandLogo.mimeType,
                                    inputWidth: creative.metadata.brandLogo.croppedAreaPixels.width,
                                    inputHeight: creative.metadata.brandLogo.croppedAreaPixels.height,
                                    outputWidth: studioConfig.formats[creative.metadata.format].brandLogo.width * 5,
                                    cropX: creative.metadata.brandLogo.croppedAreaPixels.x,
                                    cropY: creative.metadata.brandLogo.croppedAreaPixels.y,
                                    notificationDispatch,
                                    creativeDispatch,
                                    mediaLoadingDispatch,
                                  });
                                  mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
                                } catch (error) {
                                  setBrandLogoModalState(false);
                                  mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
                                  logger({ error, user, context: { origin: "Creative_PCCropper_buttonClickHandler_brandLogo" } });
                                  Google_Analytics_Event({ name: "error", category: error.message + " " + error.cause, action: "Creative_PCCropper_buttonClickHandler_brandLogo", label: user.email });
                                  return notificationDispatch({
                                    type: "ADD",
                                    payload: {
                                      header: "Error",
                                      message: "Something went wrong.",
                                      type: "error",
                                      autoDismiss: false,
                                    },
                                  });
                                }
                              }}
                            />
                          </>
                        </ComponentLibrary.PCDialogueBox>
                      </div>
                      <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={3} />

                      {/*  */}
                      {/* CONTENT */}
                      {/*  */}
                      {creative.metadata.format === "swipe" || creative.metadata.format === "post" || creative.metadata.format === "carousel" ? (
                        <>
                          <ComponentLibrary.PCContainerMain>
                            <ComponentLibrary.PCSpacer type={"padding"} position={"around"} size={4}>
                              <ComponentLibrary.PCHeader text={"Content"} />
                              <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                              <ComponentLibrary.PCDivider />
                              {/* SWIPE MATCH SCREEN TOGGLE */}
                              {creative.metadata.format === "swipe" ? (
                                <>
                                  <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                                  <div className="flex">
                                    <ComponentLibrary.PCToggle
                                      label={"Show match frame"}
                                      toggle={creative.metadata.button.hidden}
                                      handleToggle={() => {
                                        creativeDispatch({
                                          type: "UPDATE_CREATIVE_METADATA_BUTTON_HIDDEN",
                                          payload: { hidden: !creative.metadata.button.hidden },
                                        });
                                      }}
                                    />
                                  </div>
                                </>
                              ) : (
                                <></>
                              )}
                              <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                              <ContentDescription />
                            </ComponentLibrary.PCSpacer>
                          </ComponentLibrary.PCContainerMain>
                          <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={3} />
                        </>
                      ) : (
                        <></>
                      )}

                      {/*  */}
                      {/* CTA */}
                      {/*  */}
                      <ComponentLibrary.PCContainerMain>
                        <ComponentLibrary.PCSpacer type={"padding"} position={"around"} size={4}>
                          <ComponentLibrary.PCHeader text={"Call to action"} />
                          <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                          <ComponentLibrary.PCDivider />
                          {creative.metadata.format === "carousel" || creative.metadata.format === "story" ? (
                            <>
                              <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                              <div className="flex">
                                <ComponentLibrary.PCToggle
                                  label={"CTA button"}
                                  //
                                  // Toggle component handles this as 'On' = true, so we need to send down the reverse of button.hidden to this component
                                  //
                                  toggle={!creative.metadata.button.hidden}
                                  handleToggle={() =>
                                    creativeDispatch({
                                      type: "UPDATE_CREATIVE_METADATA_BUTTON_HIDDEN",
                                      payload: { hidden: !creative.metadata.button.hidden },
                                    })
                                  }
                                />
                              </div>
                            </>
                          ) : (
                            <></>
                          )}

                          {/* To show the CTA inputs we run 2 seperate checks here */}
                          {/* 1. check if the cta button toggle is true AND the format is story OR carousel */}
                          {/* OR */}
                          {/* 2. just check if the format is either post, swipe, clips, or not selected yet */}

                          {((!creative.metadata.button.hidden && (creative.metadata.format === "story" || creative.metadata.format === "carousel")) || creative.metadata.format === "post" || creative.metadata.format === "swipe" || creative.metadata.format === "clips" || creative.metadata.format === "") && (
                            <>
                              <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                              <div className="w-50">
                                <ComponentLibrary.PCInput
                                  value={creative.metadata.button.text}
                                  label={"Button text"}
                                  placeholder={"Button text"}
                                  maxChar={35}
                                  characterCounter={true}
                                  type={"text"}
                                  handleChange={(e: any) => {
                                    const { value } = e.target;
                                    creativeDispatch({
                                      type: "UPDATE_CREATIVE_METADATA_BUTTON_TEXT",
                                      payload: { text: value },
                                    });
                                  }}
                                  subHeader={"35 characters max"}
                                />
                              </div>
                              {studioConfig.formats[creative.metadata.format]?.inputs.callToAction.showFontSection && (
                                <>
                                  <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                                  <FontUpload />
                                  <ComponentLibrary.PCSpacer type={"margin"} position={"bottom"} size={5} />
                                </>
                              )}
                              {(creative.metadata.format === "swipe" || creative.metadata.format === "post" || creative.metadata.format === "story" || creative.metadata.format === "clips" || (creative.metadata.format === "carousel" && creative.metadata.aspectRatio === "mpu")) && (
                                <>
                                  <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                                  <div className="flex flex-wrap">
                                    <div className="relative">
                                      <ComponentLibrary.PCColorPicker
                                        text={"Pick colour"}
                                        label={"CTA button colour"}
                                        color={creative.metadata.button.backgroundColor}
                                        displayColorPicker={displayColorPickerCtaButton}
                                        handleClick={() => setDisplayColorPickerCtaButton(!displayColorPickerCtaButton)}
                                        handleChangeType={(e: any) => {
                                          creativeDispatch({
                                            type: "UPDATE_CREATIVE_METADATA_BUTTON_BACKGROUNDCOLOR",
                                            payload: { backgroundColor: e as unknown as string },
                                          });
                                        }}
                                        handleChange={(e: any) => {
                                          const value = e;
                                          creativeDispatch({
                                            type: "UPDATE_CREATIVE_METADATA_BUTTON_BACKGROUNDCOLOR",
                                            payload: { backgroundColor: value },
                                          });
                                        }}
                                        onClickOut={() => {
                                          setDisplayColorPickerCtaButton(false);
                                        }}
                                      />
                                    </div>
                                    <ComponentLibrary.PCSpacer type={"padding"} position={"left"} size={3} />
                                    <div className="relative">
                                      <ComponentLibrary.PCColorPicker
                                        text={"Pick colour"}
                                        label={"CTA text colour"}
                                        color={creative.metadata.button.textColor}
                                        displayColorPicker={displayColorPickerCtaText}
                                        handleClick={() => setDisplayColorPickerCtaText(!displayColorPickerCtaText)}
                                        handleChangeType={(e: any) => {
                                          creativeDispatch({
                                            type: "UPDATE_CREATIVE_METADATA_BUTTON_TEXTCOLOR",
                                            payload: { textColor: e as unknown as string },
                                          });
                                        }}
                                        handleChange={(e: any) => {
                                          const value = e;
                                          creativeDispatch({
                                            type: "UPDATE_CREATIVE_METADATA_BUTTON_TEXTCOLOR",
                                            payload: { textColor: value },
                                          });
                                        }}
                                        onClickOut={() => {
                                          setDisplayColorPickerCtaText(false);
                                        }}
                                      />
                                    </div>
                                  </div>
                                </>
                              )}
                            </>
                          )}
                          <div className="relative">
                            {(creative.metadata.format === "story" || creative.metadata.format === "post") && (
                              <>
                                <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                                <ComponentLibrary.PCDivider width={"w-100"} />
                                <ComponentLibrary.PCSpacer type={"margin"} position={"bottom"} size={4} />
                                <ComponentLibrary.PCColorPicker
                                  text={"Pick colour"}
                                  label={"Icons colour"}
                                  tooltipMessage={creative.metadata.format === "story" ? "Progress bar colour" : "Navigation icons/active dots colour"}
                                  color={creative.metadata.icons.backgroundColor}
                                  displayColorPicker={displayColorPickerIcons}
                                  handleClick={() => setDisplayColorPickerIcons(!displayColorPickerIcons)}
                                  handleChangeType={(e: any) => {
                                    creativeDispatch({
                                      type: "UPDATE_CREATIVE_METADATA_ICONS_BACKGROUNDCOLOR",
                                      payload: { backgroundColor: e as unknown as string },
                                    });
                                  }}
                                  handleChange={(e: any) => {
                                    const value = e;
                                    creativeDispatch({
                                      type: "UPDATE_CREATIVE_METADATA_ICONS_BACKGROUNDCOLOR",
                                      payload: { backgroundColor: value },
                                    });
                                  }}
                                  onClickOut={() => {
                                    setDisplayColorPickerIcons(false);
                                  }}
                                />
                              </>
                            )}
                            <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                            <ComponentLibrary.PCDivider width={"w-100"} />
                            <ComponentLibrary.PCSpacer type={"margin"} position={"bottom"} size={3} />
                            {creative.metadata.slides
                              // Shows Audio toggle if media contains video (with sound), and if format supports toggling audio
                              .map((slide: any) => slide.media.mimeType)
                              .toString()
                              .includes("video") &&
                              studioConfig.formats[creative.metadata.format].inputs.audio.audioOnToggle &&
                              creative.metadata.slides.map((slide: any) => slide.media.audioExists).includes(true) && (
                                <>
                                  <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                                  <div className="relative">
                                    <ComponentLibrary.PCToggle
                                      label={"Audio"}
                                      toggle={creative.metadata.icons.audioOn}
                                      handleToggle={() => {
                                        creative.metadata.slides.forEach((slide: any) => {
                                          if (slide.media.audioExists === true) {
                                            slide.media.audio = !creative.metadata.icons.audioOn;
                                          }
                                        });
                                        creativeDispatch({
                                          type: "UPDATE_CREATIVE_METADATA_ICONS_AUDIOON",
                                          payload: { audioOn: !creative.metadata.icons.audioOn },
                                        });
                                        /*
                                      
                                      */
                                      }}
                                    />
                                    <ComponentLibrary.PCSpacer type={"padding"} position={"bottom"} size={6} />
                                    {!creative.metadata.icons.audioOn && (
                                      <>
                                        <ComponentLibrary.PCSpacer type={"padding"} position={"top"} size={3} />
                                        <ComponentLibrary.PCDivider width={"w-100"} />
                                        <ComponentLibrary.PCSpacer type={"padding"} position={"top"} size={4} />
                                      </>
                                    )}
                                  </div>
                                </>
                              )}
                          </div>
                          {/* Shows audio icon position buttons if media contains audio, and the format supports editing the audio icon position */}
                          {creative.metadata.slides.map((slide: any) => slide.media.audio).includes(true) && studioConfig.formats[creative.metadata.format].inputs.audio.audioIconPosition && <AudioIconPosition />}
                          {/* Shows countdown icon position buttons if slide contains video, and the format supports editing the countdown icon position */}
                          {creative.metadata.slides.map((slide: any) => slide.media.mimeType).includes("video/mp4") && creative.metadata.format === "post" && <CountdownIconPosition />}
                          {/* End - hides audio position buttons when no audio */}
                          <ComponentLibrary.PCInput
                            value={creative.metadata.button.clickout}
                            label={`${!config.managedServiceTeamIds.includes(user.teamId) ? `Preview link click out URL` : `Click out URL`}`}
                            placeholder={"Paste URL"}
                            type={"text"}
                            handleChange={(e: any) => {
                              const { value } = e.target;
                              creativeDispatch({
                                type: "UPDATE_CREATIVE_METADATA_BUTTON_CLICKOUT",
                                payload: { clickout: value.trim() },
                              });
                            }}
                          />
                        </ComponentLibrary.PCSpacer>
                      </ComponentLibrary.PCContainerMain>

                      {/* BUTTON LOGO */}
                      {creative.metadata.format === "clips" || creative.metadata.format === "swipe" ? (
                        <>
                          <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={3} />
                          <ComponentLibrary.PCContainerMain>
                            <ComponentLibrary.PCSpacer type={"padding"} position={"around"} size={4}>
                              <ComponentLibrary.PCHeader text={studioConfig.formats[creative.metadata.format].inputs.buttonLogo.header} />
                              <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                              {creative.metadata.button.logo.bodyCropped === "" ? (
                                <ComponentLibrary.PCDragAndDrop
                                  label={studioConfig.formats[creative.metadata.format].inputs.buttonLogo.header}
                                  clickHandler={(e: any) => {
                                    e.target.value = null;
                                  }}
                                  changeHandler={async (e: any) => {
                                    try {
                                      [...e.target.files].forEach(async (file: any, i: any) => {
                                        e.preventDefault();
                                        if (i > 0) return;
                                        mediaLoadingDispatch({
                                          type: "UPDATE_LOADING_STATE",
                                          payload: {
                                            status: true,
                                            type: "logo",
                                            message: "Uploading footer logo",
                                            progress: Math.floor(Math.random() * (10 - 3 + 1) + 3),
                                          },
                                        });
                                        await add_button_logo({
                                          file,
                                          formatType: creative.metadata.format,
                                          teamId: user.teamId,
                                          creativeId,
                                          creativeDispatch,
                                          mimeType: file.type,
                                          name: file.name,
                                          notificationDispatch,
                                          mediaLoadingDispatch,
                                        });
                                        mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
                                      });
                                    } catch (error) {
                                      mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
                                      logger({ error, user, context: { origin: "Creative_PCDragAndDrop_changeHandler_footerLogo" } });
                                      Google_Analytics_Event({ name: "error", category: error.message + " " + error.cause, action: "Creative_PCDragAndDrop_changeHandler_footerLogo", label: user.email });
                                      return notificationDispatch({
                                        type: "ADD",
                                        payload: {
                                          header: "Error",
                                          message: "Something went wrong.",
                                          type: "error",
                                          autoDismiss: false,
                                        },
                                      });
                                    }
                                  }}
                                  dropHandler={(e: any) => {
                                    try {
                                      [...e.dataTransfer.files].forEach(async (file: any, i: any) => {
                                        mediaLoadingDispatch({
                                          type: "UPDATE_LOADING_STATE",
                                          payload: {
                                            status: true,
                                            type: "logo",
                                            message: "Uploading logo",
                                            progress: Math.floor(Math.random() * (10 - 3 + 1) + 3),
                                          },
                                        });
                                        e.preventDefault();
                                        if (i > 0) return;
                                        await add_button_logo({
                                          file,
                                          formatType: creative.metadata.format,
                                          teamId: user.teamId,
                                          creativeId,
                                          creativeDispatch,
                                          mimeType: file.type,
                                          name: file.name,
                                          notificationDispatch,
                                          mediaLoadingDispatch,
                                        });
                                        mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
                                      });
                                    } catch (error) {
                                      mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
                                      logger({ error, user, context: { origin: "Creative__PCDragAndDrop_dropHandler_footerLogo" } });
                                      Google_Analytics_Event({ name: "error", category: error.message + " " + error.cause, action: "Creative__PCDragAndDrop_dropHandler_footerLogo", label: user.email });
                                      return notificationDispatch({
                                        type: "ADD",
                                        payload: {
                                          header: "Error",
                                          message: "Something went wrong.",
                                          type: "error",
                                          autoDismiss: false,
                                        },
                                      });
                                    }
                                  }}
                                  buttonText={"Browse"}
                                  mediaAssetTitle={studioConfig.formats[creative.metadata.format].inputs.buttonLogo.header}
                                  mediaAllowed={[
                                    {
                                      title: "Images",
                                      types: ["jpg", "png"],
                                      maxSize: "2MB",
                                    },
                                  ]}
                                  multipleAllowed={false}
                                />
                              ) : (
                                <div className="flex">
                                  <div>
                                    <ComponentLibrary.PCPreviewImage height={"auto"} width={"auto"} maxWidth={"250px"} maxHeight={"200px"} backgroundImage={creative.metadata.button.logo.bodyCropped} />
                                  </div>
                                  <ComponentLibrary.PCSpacer type={"margin"} position={"left"} size={5} />
                                  <div className="flex flex-column justify-center">
                                    <div className="flex">
                                      <ComponentLibrary.PCActionButton
                                        text={"Crop image"}
                                        clickHandler={(e: any) => {
                                          e.currentTarget.blur();
                                          setButtonLogoModalState(!buttonLogoModalState);
                                        }}
                                        textColor={"color-text-5"}
                                        color={"platform-background-platform-background-4"}
                                      />
                                      <ComponentLibrary.PCSpacer type={"margin"} position={"left"} size={3} />
                                      <ComponentLibrary.PCDivider vertical={true} />
                                      <ComponentLibrary.PCSpacer type={"margin"} position={"left"} size={3} />
                                      <div className="upload-button-wrapper dim relative overflow-hidden dib-ns">
                                        <input
                                          type="file"
                                          className="o-0 absolute top-0 left-0 h-100"
                                          style={{
                                            cursor: "pointer",
                                          }}
                                          name="footerbutton_logo_upload"
                                          id="footerbutton_logo_upload"
                                          onChange={(e: any) => {
                                            mediaLoadingDispatch({
                                              type: "UPDATE_LOADING_STATE",
                                              payload: {
                                                status: true,
                                                type: "upload",
                                                message: "Uploading logo",
                                                progress: Math.floor(Math.random() * (10 - 3 + 1) + 3),
                                              },
                                            });
                                            try {
                                              add_button_logo({
                                                file: e.target.files[0],
                                                formatType: creative.metadata.format,
                                                teamId: user.teamId,
                                                creativeId,
                                                creativeDispatch,
                                                mimeType: e.target.files[0].type,
                                                name: e.target.files[0].name,
                                                notificationDispatch,
                                                mediaLoadingDispatch,
                                              });
                                              mediaLoadingDispatch({
                                                type: "UPDATE_LOADING_STATE",
                                                payload: { status: false, type: "", message: "", progress: 0 },
                                              });
                                            } catch (error) {
                                              mediaLoadingDispatch({
                                                type: "UPDATE_LOADING_STATE",
                                                payload: { status: false, type: "", message: "", progress: 0 },
                                              });
                                              logger({ error, user, context: { origin: "Creative_footerbutton_logo_upload" } });
                                              Google_Analytics_Event({ name: "error", category: error.message + " " + error.cause, action: "Creative_footerbutton_logo_upload", label: user.email });
                                              return notificationDispatch({
                                                type: "ADD",
                                                payload: {
                                                  header: "Error",
                                                  message: "Something went wrong.",
                                                  type: "error",
                                                  autoDismiss: false,
                                                },
                                              });
                                            }
                                          }}
                                          onClick={(e: any) => {
                                            e.target.value = null;
                                          }}
                                        />
                                        <ComponentLibrary.PCActionButton isDisabled={false} text={"Replace"} textColor={"color-text-5"} color={"platform-background-platform-background-4"} />
                                      </div>
                                      <ComponentLibrary.PCSpacer type={"margin"} position={"left"} size={3} />
                                      <ComponentLibrary.PCActionButton
                                        text={<ComponentLibrary.PCIconPicker icon={"trash"} />}
                                        textColor={"color-text-5"}
                                        color={"platform-background-platform-background-4"}
                                        isDisabled={!creative.metadata.button.logo.bodyCropped}
                                        clickHandler={(e: any) => {
                                          e.currentTarget.blur();
                                          creativeDispatch({
                                            type: "UPDATE_CREATIVE_METADATA_BUTTON_LOGO_CROPPEDAREAPIXELS_WIDTH",
                                            payload: { width: studioConfig.formats[creative.metadata.format].buttonLogo.width },
                                          });
                                          creativeDispatch({
                                            type: "UPDATE_CREATIVE_METADATA_BUTTON_LOGO_CROPPEDAREAPIXELS_HEIGHT",
                                            payload: { height: studioConfig.formats[creative.metadata.format].buttonLogo.height },
                                          });
                                          creativeDispatch({
                                            type: "UPDATE_CREATIVE_METADATA_BUTTON_LOGO_ZOOM",
                                            payload: { zoom: 1 },
                                          });
                                          creativeDispatch({
                                            type: "UPDATE_CREATIVE_METADATA_BUTTON_LOGO_CROPPEDAREAPIXELS_X",
                                            payload: { x: 0 },
                                          });
                                          creativeDispatch({
                                            type: "UPDATE_CREATIVE_METADATA_BUTTON_LOGO_CROPPEDAREAPIXELS_Y",
                                            payload: { y: 0 },
                                          });
                                          creativeDispatch({
                                            type: "UPDATE_CREATIVE_METADATA_BUTTON_LOGO_MIMETYPE",
                                            payload: { mimeType: "" },
                                          });
                                          creativeDispatch({
                                            type: "UPDATE_CREATIVE_METADATA_BUTTON_LOGO_ID",
                                            payload: { id: "" },
                                          });
                                          creativeDispatch({
                                            type: "UPDATE_CREATIVE_METADATA_BUTTON_LOGO_FILENAME",
                                            payload: { fileName: "" },
                                          });
                                          creativeDispatch({
                                            type: "UPDATE_CREATIVE_METADATA_BUTTON_LOGO_BODYORIGINAL",
                                            payload: { bodyOriginal: "" },
                                          });
                                          creativeDispatch({
                                            type: "UPDATE_CREATIVE_METADATA_BUTTON_LOGO_BODYCROPPED",
                                            payload: { bodyCropped: "" },
                                          });
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                              <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={5} />
                              <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                            </ComponentLibrary.PCSpacer>
                          </ComponentLibrary.PCContainerMain>
                          {/* Button Logo Crop Modal */}
                          <div className="relative">
                            <ComponentLibrary.PCDialogueBox open={buttonLogoModalState} width={"width-13"} height={"height-13"} onClose={() => setButtonLogoModalState(!buttonLogoModalState)}>
                              <>
                                <ComponentLibrary.PCCropper
                                  media={creative.metadata.button.logo.bodyOriginal || placeholderButtonLogo}
                                  cropShape={studioConfig.formats[creative.metadata.format].buttonLogo.cropShape}
                                  mimeType={creative.metadata.button.logo.mimeType}
                                  crop={creative.metadata.button.logo.crop}
                                  zoom={creative.metadata.button.logo.zoom}
                                  aspect={creative.metadata.button.logo.dimensions.width / creative.metadata.button.logo.dimensions.height}
                                  onCropChange={(crop: any) => {
                                    creativeDispatch({
                                      type: "UPDATE_CREATIVE_METADATA_BUTTON_LOGO_CROP_X",
                                      payload: { x: crop.x },
                                    });
                                    creativeDispatch({
                                      type: "UPDATE_CREATIVE_METADATA_BUTTON_LOGO_CROP_Y",
                                      payload: { y: crop.y },
                                    });
                                  }}
                                  onZoomChange={(zoom: any) => {
                                    creativeDispatch({
                                      type: "UPDATE_CREATIVE_METADATA_BUTTON_LOGO_ZOOM",
                                      payload: { zoom },
                                    });
                                  }}
                                  onCropComplete={(croppedArea: any, croppedAreaPixels: any) => {
                                    creativeDispatch({
                                      type: "UPDATE_CREATIVE_METADATA_BUTTON_LOGO_CROPPEDAREAPIXELS_WIDTH",
                                      payload: { width: croppedAreaPixels.width },
                                    });
                                    creativeDispatch({
                                      type: "UPDATE_CREATIVE_METADATA_BUTTON_LOGO_CROPPEDAREAPIXELS_HEIGHT",
                                      payload: { height: croppedAreaPixels.height },
                                    });
                                    creativeDispatch({
                                      type: "UPDATE_CREATIVE_METADATA_BUTTON_LOGO_CROPPEDAREAPIXELS_X",
                                      payload: { x: croppedAreaPixels.x },
                                    });
                                    creativeDispatch({
                                      type: "UPDATE_CREATIVE_METADATA_BUTTON_LOGO_CROPPEDAREAPIXELS_Y",
                                      payload: { y: croppedAreaPixels.y },
                                    });
                                  }}
                                  buttonClickHandler={async () => {
                                    try {
                                      mediaLoadingDispatch({
                                        type: "UPDATE_LOADING_STATE",
                                        payload: {
                                          status: true,
                                          type: "crop",
                                          message: "Cropping logo",
                                          progress: Math.floor(Math.random() * (10 - 3 + 1) + 3),
                                        },
                                      });
                                      setButtonLogoModalState(false);
                                      await edit_button_logo({
                                        method: "crop",
                                        teamId: user.teamId,
                                        creativeId: creative.creativeId,
                                        fileName: creative.metadata.button.logo.fileName,
                                        fileId: creative.metadata.button.logo.id,
                                        mimeType: creative.metadata.button.logo.mimeType,
                                        inputWidth: creative.metadata.button.logo.croppedAreaPixels.width,
                                        inputHeight: creative.metadata.button.logo.croppedAreaPixels.height,
                                        outputWidth: studioConfig.formats[creative.metadata.format].buttonLogo.width * 5,
                                        cropX: creative.metadata.button.logo.croppedAreaPixels.x,
                                        cropY: creative.metadata.button.logo.croppedAreaPixels.y,
                                        notificationDispatch,
                                        creativeDispatch,
                                        mediaLoadingDispatch,
                                      });
                                      mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
                                    } catch (error) {
                                      setButtonLogoModalState(false);
                                      mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
                                      logger({ error, user, context: { origin: "Creative_PCCropper_buttonClickHandler_brandLogo" } });
                                      Google_Analytics_Event({ name: "error", category: error.message + " " + error.cause, action: "Creative_PCCropper_buttonClickHandler_brandLogo", label: user.email });
                                      return notificationDispatch({
                                        type: "ADD",
                                        payload: {
                                          header: "Error",
                                          message: "Something went wrong.",
                                          type: "error",
                                          autoDismiss: false,
                                        },
                                      });
                                    }
                                  }}
                                />
                              </>
                            </ComponentLibrary.PCDialogueBox>
                          </div>
                        </>
                      ) : (
                        <></>
                      )}
                      {/*  */}
                      {/* FRAMES */}
                      {/*  */}
                      {creative.metadata.format && (
                        <>
                          <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={3} />
                          <ComponentLibrary.PCContainerMain>
                            <ComponentLibrary.PCSpacer type={"padding"} position={"around"} size={4}>
                              <div className="flex nowrap">
                                <ComponentLibrary.PCHeader text={"Frames"} />
                                <ComponentLibrary.PCCaption text={studioConfig.formats[creative.metadata.format].inputs.frames.frameCaption} fontWeight={"5"} />
                              </div>
                              <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                              <ComponentLibrary.PCDragAndDrop
                                label={"Frame media"}
                                changeHandler={async (e: any) => {
                                  /**
                                   * HTL (13-06-23)
                                   * Replaced .forEach with Promise.allSettled to handle errors better, where one failed upload won't stop the entire process.
                                   */
                                  await Promise.allSettled(
                                    [...e.target.files].map(async (file: any, i: any) => {
                                      try {
                                        e.preventDefault();
                                        mediaLoadingDispatch({
                                          type: "UPDATE_LOADING_STATE",
                                          payload: {
                                            status: true,
                                            type: "upload",
                                            message: "Uploading assets",
                                            progress: Math.floor(Math.random() * (10 - 3 + 1) + 3),
                                          },
                                        });
                                        let fileNumber = creative.metadata.slides.length + i;
                                        /**
                                         * AF (4/10/23)
                                         * if there is more than 1 frame
                                         * OR if there is only 1 frame but it has an asset already then increase fileNumber
                                         */
                                        if (creative.metadata.slides.length > 1 || (creative.metadata.slides.length === 1 && firstSlide.media.bodyOriginal)) {
                                          fileNumber++;
                                        }
                                        if (fileNumber > studioConfig.formats[creative.metadata.format].maxSlides) {
                                          mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
                                          return notificationDispatch({
                                            type: "ADD",
                                            payload: {
                                              header: "Error",
                                              message: "Max Slides Already Reached",
                                              type: "error",
                                              autoDismiss: false,
                                            },
                                          });
                                        }
                                        if (fileNumber > 10) {
                                          mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
                                          return;
                                        }
                                        if (fileNumber !== 1) {
                                          creativeDispatch({
                                            type: "ADD_NEW_SLIDE",
                                            payload: { slideNumber: fileNumber },
                                          });
                                        }
                                        const uploadedAssetStatus = await add_asset({
                                          file,
                                          slideNumber: fileNumber,
                                          creativeDispatch,
                                          formatType: creative.metadata.format,
                                          aspectRatio: creative.metadata.aspectRatio,
                                          mimeType: file.type,
                                          name: file.name,
                                          teamId: user.teamId,
                                          creativeId: creative.creativeId,
                                          mediaLoadingDispatch,
                                        });
                                        mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
                                        return uploadedAssetStatus;
                                      } catch (error) {
                                        mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
                                        logger({ error, user, context: { origin: "Creative_PCDragAndDrop_changeHandler_asset" } });
                                        Google_Analytics_Event({ name: "error", category: error.message + " " + error.cause, action: "Creative_PCDragAndDrop_changeHandler_asset", label: user.email });
                                        return notificationDispatch({
                                          type: "ADD",
                                          payload: {
                                            header: error.message,
                                            message: error.cause,
                                            type: "error",
                                            autoDismiss: false,
                                          },
                                        });
                                      }
                                    }),
                                  );
                                }}
                                dropHandler={async (e: any) => {
                                  /**
                                   * HTL (13-06-23)
                                   * .forEach has been replaced with Promise.allSettled(map => ()) as a better way to handle promised return values.
                                   * The promised file uploads aren't all required for further use if one fails to upload.
                                   */
                                  await Promise.allSettled(
                                    [...e.dataTransfer.files].map(async (file: any, i: any) => {
                                      try {
                                        e.preventDefault();
                                        mediaLoadingDispatch({
                                          type: "UPDATE_LOADING_STATE",
                                          payload: {
                                            status: true,
                                            type: "upload",
                                            message: "Uploading assets",
                                            progress: Math.floor(Math.random() * (10 - 3 + 1) + 3),
                                          },
                                        });
                                        let fileNumber = creative.metadata.slides.length + i;
                                        /**
                                         * AF (4/10/23)
                                         * if there is more than 1 frame
                                         * OR if there is only 1 frame but it has an asset already then increase fileNumber
                                         */
                                        if (creative.metadata.slides.length > 1 || (creative.metadata.slides.length === 1 && firstSlide.media.bodyOriginal)) {
                                          fileNumber++;
                                        }
                                        if (fileNumber > studioConfig.formats[creative.metadata.format].maxSlides) {
                                          mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
                                          return notificationDispatch({
                                            type: "ADD",
                                            payload: {
                                              header: "Error",
                                              message: "Max Slides Already Reached",
                                              type: "error",
                                              autoDismiss: false,
                                            },
                                          });
                                        }
                                        if (fileNumber > 10) {
                                          mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
                                          return;
                                        }
                                        if (fileNumber !== 1) {
                                          creativeDispatch({
                                            type: "ADD_NEW_SLIDE",
                                            payload: { slideNumber: fileNumber },
                                          });
                                        }
                                        const uploadedAssetStatus = await add_asset({
                                          file,
                                          slideNumber: fileNumber,
                                          creativeDispatch,
                                          formatType: creative.metadata.format,
                                          aspectRatio: creative.metadata.aspectRatio,
                                          mimeType: file.type,
                                          name: file.name,
                                          teamId: user.teamId,
                                          creativeId: creative.creativeId,
                                          mediaLoadingDispatch,
                                        });
                                        mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
                                        return uploadedAssetStatus;
                                      } catch (error) {
                                        mediaLoadingDispatch({ type: "UPDATE_LOADING_STATE", payload: { status: false, type: "", message: "", progress: 0 } });
                                        logger({ error, user, context: { origin: "Creative_PCDragAndDrop_dropHandler_asset" } });
                                        Google_Analytics_Event({ name: "error", category: error.message + " " + error.cause, action: "Creative_PCDragAndDrop_dropHandler_asset", label: user.email });
                                        return notificationDispatch({
                                          type: "ADD",
                                          payload: {
                                            header: error.message,
                                            message: error.cause,
                                            type: "error",
                                            autoDismiss: false,
                                          },
                                        });
                                      }
                                    }),
                                  );
                                }}
                                clickHandler={(e: any) => {
                                  e.target.value = null;
                                }}
                                buttonText={"Browse"}
                                mediaAssetTitle={"Media"}
                                mediaAllowed={
                                  creative.metadata.format === "post" || creative.metadata.format === "story"
                                    ? [
                                        { title: "Images", types: ["jpg", "png"], maxSize: "40MB" },
                                        { title: "Videos", types: ["mp4"], maxSize: "40MB" },
                                      ]
                                    : creative.metadata.format === "carousel" || creative.metadata.format === "swipe"
                                    ? [{ title: "Images", types: ["jpg", "png"], maxSize: "40MB" }]
                                    : creative.metadata.format === "clips"
                                    ? [{ title: "Videos", types: ["mp4"], maxSize: "40MB" }]
                                    : []
                                }
                                multipleAllowed={true}
                              />
                              <ComponentLibrary.PCSpacer type="margin" position="top" size={5} />
                              <ComponentLibrary.PCDivider />
                              <ComponentLibrary.PCSpacer type="margin" position="top" size={5} />
                              <FrameRatioBar adSize={adSize} />
                            </ComponentLibrary.PCSpacer>
                          </ComponentLibrary.PCContainerMain>
                        </>
                      )}
                      {renderChosenFormatFramesSection()}
                        </>
                      )}
                    </div>
                    <ComponentLibrary.PCSpacer type={"margin"} position={"left"} size={3} />

                    {/*  */}
                    {/* RIGHT COLUMN */}
                    {/*  */}
                    <div id="creative-right-column" className="w-50">
                      <ComponentLibrary.PCContainerMain color={"background-color-neutral-2"} extraClasses={"h-100 items-center"}>
                        <div className="flex flex-column items-center h-100" id="right-col-inner">
                          <div style={isFormatPreviewSticky ? { position: "sticky", top: 50 } : {}}>
                            <ComponentLibrary.PCSpacer type={"margin"} position={"around"} size={3}>
                              <div className="tc">
                                <ComponentLibrary.PCLabel text={"PREVIEW"} letterSpacing={"1px"} />
                              </div>
                            </ComponentLibrary.PCSpacer>
                            {/* RENDER FORMAT PREVIEW */}
                            <div id="format-preview-container">
                              <ComponentLibrary.PCSpacer type={"padding"} position={"around"} size={2}>
                                {buildMethod === "custom" ? <CustomPackagePreview pkg={customPackage} /> : creative.metadata.format ? <DynamicFormatPreview /> : <></>}
                              </ComponentLibrary.PCSpacer>
                            </div>
                            <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                            {buildMethod !== "custom" && !creative.metadata.format ? (
                              <div className="flex flex-column justify-center tc">
                                <img style={{ width: 320 }} src={PlaceholderCreative} />
                                <ComponentLibrary.PCSpacer type={"margin"} position={"around"} size={5}>
                                  <ComponentLibrary.PCLabel text={"Select ad format to see preview"} />
                                </ComponentLibrary.PCSpacer>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </ComponentLibrary.PCContainerMain>
                    </div>
                  </div>
                  <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={5} />

                  {/*  */}
                  {/* BOTTOM BAR */}
                  {/*  */}
                  <div className="flex flex-column items-center">
                    <div className="w-100">
                      <ComponentLibrary.PCDivider />
                      <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={5} />
                      <div className="flex">
                        <div className="w-100">
                          <ComponentLibrary.PCCaption text={"Manage ad"} fontWeight={"4"} />
                        </div>
                        <ExportPngButton />
                        <ComponentLibrary.PCSpacer type={"padding"} position={"left"} size={3} />
                        <PreviewButton
                          isTruncated={false}
                          data={{
                            advertiserId: creative.advertiserId,
                            advertiserName: creative.advertiserName,
                            id: creativeId,
                            adName: creative.adName,
                            metadata: {
                              aspectRatio: creative.metadata.aspectRatio,
                              brandName: creative.metadata.brandName,
                              format: creative.metadata.format,
                              slides: creative.metadata.slides ? creative.metadata.slides.filter((x) => x?.media.mimeType) : [],
                            },
                          }}
                        />
                        <ComponentLibrary.PCSpacer type={"padding"} position={"left"} size={3} />
                        <SaveButton />
                        <ComponentLibrary.PCSpacer type={"padding"} position={"left"} size={3} />
                        <ComponentLibrary.PCActionButton isDisabled={isNewCreative} text={"Request Tags"} color={"background-color-primary-3"} textColor={"white"} iconRight={"arrow-right"} clickHandler={(e) => handleRequestTag(e, requestTags)} />
                      </div>
                    </div>
                  </div>
                  {/*  */}
                  {/* Adform Tagging modal */}
                  {/*  */}
                  <div className="relative">
                    <ComponentLibrary.PCDialogueBox
                      open={requestTagsModalState}
                      width={"width-13"}
                      onClose={() => {
                        setAdformTaggingInputs(emptyAdformTaggingInput);
                        setRequestTagsModalState(!requestTagsModalState);
                      }}>
                      <>
                        <ComponentLibrary.PCHeader text="Set up your tagging" />
                        <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={3} />
                        <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={3} />
                        {/* Destination URL */}
                        <ComponentLibrary.PCInput
                          label={"Click Tracking Tag (Destination URL) *"}
                          type={"text"}
                          handleChange={async (e: any) => {
                            setAdformTaggingInputs({ ...adformTaggingInputs, destinationUrl: { value: e.target.value, valid: false, errorMessage: "" } });
                          }}
                          handleBlur={() => {
                            setAdformTaggingInputs({
                              ...adformTaggingInputs,
                              destinationUrl: validate_adform_url(adformTaggingInputs.destinationUrl.value, true),
                            });
                          }}
                          isError={adformTaggingInputs.destinationUrl.errorMessage === "" ? false : true}
                          errorMessage={adformTaggingInputs.destinationUrl.errorMessage}
                        />
                        <ComponentLibrary.PCLabel text="* required input."></ComponentLibrary.PCLabel>
                        <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={3} />
                        {/* Impression Tracking Pixel */}
                        <ComponentLibrary.PCInput
                          label={"Impression Tracking Pixel"}
                          type={"text"}
                          handleChange={async (e: any) => {
                            setAdformTaggingInputs({ ...adformTaggingInputs, impressionPixel: { value: e.target.value, valid: false, errorMessage: "" } });
                          }}
                          handleBlur={() => {
                            setAdformTaggingInputs({
                              ...adformTaggingInputs,
                              impressionPixel: validate_adform_url(adformTaggingInputs.impressionPixel.value, false),
                            });
                          }}
                          isError={adformTaggingInputs.impressionPixel.errorMessage === "" ? false : true}
                          errorMessage={adformTaggingInputs.impressionPixel.errorMessage}
                        />
                        <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={3} />
                        {/* Click Pixel */}
                        <ComponentLibrary.PCInput
                          label={"Click Pixel"}
                          type={"text"}
                          handleChange={async (e: any) => {
                            setAdformTaggingInputs({ ...adformTaggingInputs, clickPixel: { value: e.target.value, valid: false, errorMessage: "" } });
                          }}
                          handleBlur={() => {
                            setAdformTaggingInputs({ ...adformTaggingInputs, clickPixel: validate_adform_url(adformTaggingInputs.clickPixel.value, false) });
                          }}
                          isError={adformTaggingInputs.clickPixel.errorMessage === "" ? false : true}
                          errorMessage={adformTaggingInputs.clickPixel.errorMessage}
                        />
                        <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={3} />
                        {/* JavaScript URL */}
                        <ComponentLibrary.PCInput
                          label={"JavaScript URL"}
                          type={"text"}
                          handleChange={async (e: any) => {
                            setAdformTaggingInputs({ ...adformTaggingInputs, javascriptUrl: { value: e.target.value, valid: false, errorMessage: "" } });
                          }}
                          handleBlur={() => {
                            setAdformTaggingInputs({
                              ...adformTaggingInputs,
                              javascriptUrl: validate_adform_url(adformTaggingInputs.javascriptUrl.value, false),
                            });
                          }}
                          isError={adformTaggingInputs.javascriptUrl.errorMessage === "" ? false : true}
                          errorMessage={adformTaggingInputs.javascriptUrl.errorMessage}
                        />
                        <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                        <div className="flex">
                          <div className="w-100">
                            <ComponentLibrary.PCActionButton
                              text="Apply &amp; Request Tags"
                              iconRight={"arrow-right"}
                              textColor={"white"}
                              color={"background-color-primary-3"}
                              iconColor={"white"}
                              width={"w-100"}
                              clickHandler={async (e) => {
                                if (adformTaggingInputs.impressionPixel.valid && adformTaggingInputs.clickPixel.valid && adformTaggingInputs.javascriptUrl.valid && adformTaggingInputs.destinationUrl.valid) {
                                  await requestAdformTag(e);
                                } else {
                                  return notificationDispatch({
                                    type: "ADD",
                                    payload: {
                                      header: "Error",
                                      message: "Adform Tagging inputs must be valid urls.",
                                      type: "error",
                                      autoDismiss: false,
                                    },
                                  });
                                }
                              }}
                            />
                          </div>
                        </div>
                      </>
                    </ComponentLibrary.PCDialogueBox>
                  </div>
                  {/*  */}
                  {/* Adform Tagging Ready Now modal */}
                  {/*  */}
                  <div className="relative">
                    <ComponentLibrary.PCDialogueBox
                      open={requestTagsSizeModalState}
                      width={"width-13"}
                      height={"height-13"}
                      onClose={() => {
                        setRequestTagsSizeModalState(!requestTagsSizeModalState);
                      }}>
                      <>
                        <div className="flex items-center">
                          <ComponentLibrary.PCIconPicker icon={"check-circle"} color={"color-secondary-4"} size={"2x"} />
                          <ComponentLibrary.PCSpacer type={"margin"} position={"right"} size={3} />
                          <ComponentLibrary.PCHeader text="Your tag is now ready" />
                        </div>
                        <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                        <ComponentLibrary.PCSlider optionsList={["Short Tags (default)", "Long Tags"]} width={"100%"} height={"40px"} textSize={"text-size-2"} setSelectedValue={setRequestTagsToggleSelectedValue} />
                        <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={5} />
                        <ComponentLibrary.PCSubHeader text={"Copy snippet code"} />
                        <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={3} />
                        <ComponentLibrary.PCTextArea rows={7} value={requestTagsSnippetTextAreaValue[selectedTag]} id={"text-input"} placeholder={"This is a simple text area..."} spellCheck={false} />
                        <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={3} />
                        <div className="w-100">
                          <ComponentLibrary.PCActionButton text="Copy to clipboard" iconLeft={"clone"} textColor={"white"} color={"background-color-primary-3"} iconColor={"white"} width={"100%"} clickHandler={(e) => copyAdFormContentToClipboard(e, requestTagsSnippetTextAreaValue[selectedTag], selectedTag)} />
                        </div>
                        <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={5} />
                        <ComponentLibrary.PCSubHeader text={"Download Tags"} />
                        <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={3} />
                        <div className="w-100">
                          <ComponentLibrary.PCActionButton text="Download Tags" iconRight={"arrow-right"} textColor={"white"} color={"background-color-primary-3"} iconColor={"white"} width={"100%"} clickHandler={async (e) => await handleRequestTag(e, requestSavedTags[selectedTag])} />
                        </div>
                        <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                        <ComponentLibrary.PCDivider />
                        <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
                        <div className="w-100">
                          <ComponentLibrary.PCActionButton text="Ok, I'm done" textColor={"color-text-5"} width={"100%"} clickHandler={() => setRequestTagsSizeModalState(!requestTagsSizeModalState)} />
                        </div>
                      </>
                    </ComponentLibrary.PCDialogueBox>
                  </div>
                  <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={3} />
                </div>
              </ComponentLibrary.PCSpacer>
            </ComponentLibrary.PCSpacer>
          </ComponentLibrary.PCSpacer>
          {/* 
          NPS Scorer HTL 28-04-23
          */}
          <NPS question={"How likely would you be to recommend this platform to a friend at another company?"} submitMessage={"Thank you for your feedback!"} />
        </div>
      )}
    </>
  );
}
